import { Component, inject, input, OnInit, TemplateRef, viewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';

import { take } from 'rxjs/operators';

import { OcJsonSchemaValidatorAjv } from '../../../../../core/oc-validator/oc-jsonSchemaValidatorAjv.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IError } from '@orgacard/login';

import { AppService } from '../../../../../app.service';
import { CoreService } from '../../../../../core/core.service';
import { EnvironmentService } from '../../../../../core/environment.service';
import { ocValidator } from '../../../../../core/oc-validator/oc.validator';
import { ModalService } from '../../../../../core/services/modal.service';
import { UtilityHelperService } from '../../../../../core/utility-helper.service';
import { UtilityHelper } from '../../../../../core/utility.helper';
import { LoadingOverlayService } from '../../../../../loading-overlay/loading-overlay.service';
import { SystemMethod } from '../../../../shared/models/basic.model';
import { ISupplierDiscount } from '../../../shared/models/supplier.model';
import { SupplierService } from '../../../shared/services/supplier.service';

@UntilDestroy()
@Component({
    selector: 'app-discounts',
    templateUrl: './discounts.component.html',
    standalone: false,
})
export class DiscountsComponent implements OnInit {
    private readonly utilityHelperService = inject(UtilityHelperService);
    coreService = inject(CoreService);
    environmentService = inject(EnvironmentService);
    private appService = inject(AppService);
    ocJsonSchemaValidatorAjv = inject(OcJsonSchemaValidatorAjv);
    supplierService = inject(SupplierService);
    formBuilder = inject(UntypedFormBuilder);
    modalService = inject(ModalService);
    loadingOverlayService = inject(LoadingOverlayService);

    public readonly discountModal = viewChild<TemplateRef<any>>('discountModal');
    public readonly discountMessageModal = viewChild<TemplateRef<any>>('discountMessageModal');

    public readonly updateable = input<boolean>(true);
    public readonly privilegeContext = input<string>(''); // CASL Privileges: in welchem Context z.B. supplierBasedata etc. müssen die Privilegien geprüft werden
    public readonly showButtons = input<boolean>(true);
    public readonly showHeadline = input<boolean>(true);

    public utilityHelper = new UtilityHelper(this.appService, this.environmentService, this.coreService, this.loadingOverlayService);

    public displayedColumns = ['typedescr', 'condition', 'conditionValue', 'discountValue', 'isDiscountPercent'];
    public conditions: string[] = ['>', '>='];

    public discountForm: UntypedFormGroup;

    // convenience getter for easy access to form fields
    public get form() {
        return this.discountForm.controls;
    }

    public discountModalHeader: string;
    public discountModalBody: string;

    public discountModalMode: SystemMethod;

    public tempDiscountId: number;

    public ngOnInit() {
        this.initDiscounts();
        //
        if (this.showButtons()) {
            this.displayedColumns.push('buttons');
        }
    }

    public initDiscounts() {
        this.loadingOverlayService.startLoading();
        this.supplierService
            .getDiscounts()
            .pipe(take(1))
            .subscribe(
                (response) => {
                    this.initDiscountForm(undefined);
                    this.loadingOverlayService.stopLoading();
                },
                (err: IError) => {
                    this.utilityHelperService.handleComponentError(err);
                },
            );
    }

    public openEditDiscountModal(supplierDiscount: ISupplierDiscount) {
        this.discountModalHeader = this.coreService.getMsgkey('mws.discount.editdiscount');
        this.initDiscountForm(supplierDiscount);
        this.discountModalMode = SystemMethod.EDIT;
        this.openDiscountModal(this.discountModal());
    }

    public openNewDiscountModal() {
        this.discountModalHeader = this.coreService.getMsgkey('mws.discount.creatediscount');
        this.initDiscountForm(undefined);
        this.discountModalMode = SystemMethod.NEW;
        this.openDiscountModal(this.discountModal());
    }

    public openDiscountModal(modal: TemplateRef<any>) {
        this.modalService.openModal(modal);
    }

    public openDeleteModal(discountId: number) {
        this.tempDiscountId = discountId;
        this.discountModalHeader = this.coreService.getMsgkey('mws.discount.deletediscount');
        this.discountModalBody = this.coreService.getMsgkey('mws.discount.deletediscounttext');
        this.discountModalMode = SystemMethod.DELETE;
        this.openDiscountModal(this.discountMessageModal());
    }

    public getDiscountFromArray(id: number) {
        for (const discount of this.supplierService.discounts) {
            if (discount.id === id) {
                return discount;
            }
        }
    }

    public initDiscountForm(supplierDiscount: ISupplierDiscount) {
        if (this.supplierService.supplierContract) {
            this.discountForm = this.formBuilder.group({
                id: supplierDiscount !== undefined ? supplierDiscount.id : undefined,
                supplierContractId: this.supplierService.supplierContract.id,
                attributeLocale: this.environmentService.systemUser.language,
                discount: supplierDiscount !== undefined ? supplierDiscount.discount.id : this.supplierService.discounts[0].id,
                condition: [
                    {
                        value: supplierDiscount !== undefined ? supplierDiscount.condition : this.conditions[0],
                        disabled: !this.coreService.checkAbility(
                            supplierDiscount !== undefined ? 'create' : 'update',
                            this.privilegeContext() + 'discount.condition',
                        ),
                    },
                    [ocValidator(this.ocJsonSchemaValidatorAjv, this.environmentService, this.privilegeContext() + 'Discount', 'condition')],
                ],
                conditionValue: [
                    {
                        value: supplierDiscount?.conditionValue,
                        disabled: !this.coreService.checkAbility(
                            supplierDiscount !== undefined ? 'create' : 'update',
                            this.privilegeContext() + 'discount.conditionvalue',
                        ),
                    },
                    [
                        ocValidator(
                            this.ocJsonSchemaValidatorAjv,
                            this.environmentService,
                            this.privilegeContext() + 'Discount',
                            'conditionValue',
                            this.coreService.getMsgkey('mws.discount.conditionvalue', undefined),
                        ),
                    ],
                ],
                discountValue: [
                    {
                        value: supplierDiscount !== undefined ? supplierDiscount.discountValue : undefined,
                        disabled: !this.coreService.checkAbility(
                            supplierDiscount !== undefined ? 'create' : 'update',
                            this.privilegeContext() + 'discount.discountvalue',
                        ),
                    },
                    [
                        ocValidator(
                            this.ocJsonSchemaValidatorAjv,
                            this.environmentService,
                            this.privilegeContext() + 'Discount',
                            'discountValue',
                            this.coreService.getMsgkey('mws.discount.discountvalue', undefined),
                        ),
                    ],
                ],
                isDiscountPercent: [
                    {
                        value: supplierDiscount !== undefined ? supplierDiscount.isDiscountPercent : false,
                        disabled: !this.coreService.checkAbility(
                            supplierDiscount !== undefined ? 'create' : 'update',
                            this.privilegeContext() + 'discount.isdiscountpercent',
                        ),
                    },
                    [ocValidator(this.ocJsonSchemaValidatorAjv, this.environmentService, this.privilegeContext() + 'Discount', 'isDiscountPercent')],
                ],
                changed: false,
            });
            this.discountForm.valueChanges.pipe(untilDestroyed(this)).subscribe((change) => {
                // PRÜFEN OB FORM VALID IST !!! NICHT VIA this.xxxxxxForm.valid
                // PRÜFEN OB FORM VALID IST !!! NICHT VIA this.xxxxxxForm.valid
                const result = this.ocJsonSchemaValidatorAjv.validateData(this.privilegeContext() + 'Discount', this.discountForm.value);
                if (!this.discountForm.get('changed').touched && result.isValid) {
                    this.discountForm.get('changed').markAsTouched();
                } else if (!result.isValid) {
                    this.discountForm.get('changed').markAsUntouched();
                }
            });
        }
    }

    public saveDiscountModal() {
        // PRÜFEN OB FORM VALID IST !!! NICHT VIA this.xxxxxxForm.valid
        // PRÜFEN OB FORM VALID IST !!! NICHT VIA this.xxxxxxForm.valid
        const result = this.ocJsonSchemaValidatorAjv.validateData(this.privilegeContext() + 'Discount', this.discountForm.value);
        if (result.isValid) {
            this.loadingOverlayService.startLoading();
            const tempDiscount: ISupplierDiscount = this.getSupplierDiscountFromForm();
            this.supplierService
                .saveSupplierDiscount(tempDiscount)
                .pipe(take(1))
                .subscribe(
                    (supplierDiscountResponse) => {
                        switch (this.discountModalMode) {
                            case SystemMethod.NEW:
                                this.supplierService.supplierContractDetail.supplierDiscounts.push(supplierDiscountResponse);
                                break;
                            case SystemMethod.EDIT:
                                const indexElement = this.supplierService.supplierContractDetail.supplierDiscounts.findIndex(
                                    (sDiscount) => sDiscount.id === tempDiscount.id,
                                );
                                this.supplierService.supplierContractDetail.supplierDiscounts[indexElement] = supplierDiscountResponse;
                                break;
                        }
                        this.supplierService.supplierContractDetail.supplierDiscounts = this.supplierService.supplierContractDetail.supplierDiscounts.concat();
                        this.loadingOverlayService.stopLoading();
                    },
                    (err: IError) => {
                        this.utilityHelperService.handleComponentError(err);
                    },
                );
        } else {
            this.appService.showToast('error', 'ERROR:', 'DiscountForm NOT VALID');
        }
    }

    public deleteDiscount() {
        this.loadingOverlayService.startLoading();
        this.supplierService
            .deleteSupplierDiscount(this.tempDiscountId)
            .pipe(take(1))
            .subscribe(
                (response) => {
                    // const indexElement = this.supplierService.supplierContractDetail.supplierDiscounts.findIndex(sDiscount => sDiscount.id === this.tempDiscountId);
                    // this.supplierService.supplierContractDetail.supplierDiscounts.splice(indexElement, 1);
                    // this.supplierService.supplierContractDetail.supplierDiscounts = this.supplierService.supplierContractDetail.supplierDiscounts.concat();
                    this.loadingOverlayService.stopLoading();
                },
                (err: IError) => {
                    this.utilityHelperService.handleComponentError(err);
                },
            );
    }

    public getSupplierDiscountFromForm(): ISupplierDiscount {
        return {
            id: this.discountForm.get('id').value,
            supplierId: this.supplierService.supplierId,
            supplierContractId: this.supplierService.supplierContract.id,
            isDiscountPercent: this.discountForm.get('isDiscountPercent').value,
            condition: this.discountForm.get('condition').value,
            conditionValue: this.discountForm.get('conditionValue').value,
            discountValue: this.discountForm.get('discountValue').value,
            discount: this.getDiscountFromArray(this.discountForm.get('discount').value),
            attributeLocale: this.environmentService.systemUser.language,
        };
    }
}
